import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  SelectField,
  RichTextField,
  TopToolbar,
  EditButton,
  useRecordContext,
} from 'react-admin';
import {
  TicketStatus,
  TicketPriority,
  TicketCategory,
} from '../../generated/data-contracts';
import { Chip, Box, Typography } from '@mui/material';

// Componente para renderizar el estado con colores
const StatusField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const statusColors = {
    [TicketStatus.Open]: 'primary',
    [TicketStatus.InProgress]: 'secondary',
    [TicketStatus.Resolved]: 'success',
    [TicketStatus.Closed]: 'default',
    [TicketStatus.Cancelled]: 'error',
  } as const;

  const statusLabels = {
    [TicketStatus.Open]: 'Abierto',
    [TicketStatus.InProgress]: 'En Progreso',
    [TicketStatus.Resolved]: 'Resuelto',
    [TicketStatus.Closed]: 'Cerrado',
    [TicketStatus.Cancelled]: 'Cancelado',
  };

  return (
    <Chip
      label={statusLabels[record.status as TicketStatus] || record.status}
      color={statusColors[record.status as TicketStatus] || 'default'}
      size='medium'
    />
  );
};

// Componente para renderizar la prioridad con colores
const PriorityField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const priorityColors = {
    [TicketPriority.Low]: 'default',
    [TicketPriority.Medium]: 'primary',
    [TicketPriority.High]: 'warning',
    [TicketPriority.Critical]: 'error',
  } as const;

  const priorityLabels = {
    [TicketPriority.Low]: 'Baja',
    [TicketPriority.Medium]: 'Media',
    [TicketPriority.High]: 'Alta',
    [TicketPriority.Critical]: 'Crítica',
  };

  return (
    <Chip
      label={
        priorityLabels[record.priority as TicketPriority] || record.priority
      }
      color={priorityColors[record.priority as TicketPriority] || 'default'}
      size='medium'
    />
  );
};

const TicketShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

export const TicketsShow: React.FC = () => (
  <Show actions={<TicketShowActions />} title='Detalles del Ticket'>
    <SimpleShowLayout>
      <Box display='flex' flexDirection='column' gap={2}>
        <Box>
          <Typography variant='h6' gutterBottom>
            Información General
          </Typography>
          <TextField source='id' label='ID' />
          <TextField source='title' label='Título' />
          <Box display='flex' gap={2} alignItems='center' my={1}>
            <Box>
              <Typography variant='body2' color='textSecondary'>
                Estado:
              </Typography>
              <StatusField />
            </Box>
            <Box>
              <Typography variant='body2' color='textSecondary'>
                Prioridad:
              </Typography>
              <PriorityField />
            </Box>
          </Box>
          <SelectField
            source='category'
            label='Categoría'
            choices={[
              { id: TicketCategory.Bug, name: 'Error' },
              { id: TicketCategory.Feature, name: 'Funcionalidad' },
              { id: TicketCategory.Support, name: 'Soporte' },
              { id: TicketCategory.Contact, name: 'Contacto' },
              { id: TicketCategory.General, name: 'General' },
            ]}
          />
        </Box>

        <Box>
          <Typography variant='h6' gutterBottom>
            Descripción
          </Typography>
          <TextField source='description' label='Descripción' />
        </Box>

        <Box>
          <Typography variant='h6' gutterBottom>
            Asignación y Usuario
          </Typography>
          <TextField source='createdByUserName' label='Creado por' />
          <TextField
            source='createdByUserFullName'
            label='Nombre completo del creador'
          />
          <TextField source='assignedToUserName' label='Asignado a' />
          <TextField
            source='assignedToUserFullName'
            label='Nombre completo del asignado'
          />
        </Box>

        <Box>
          <Typography variant='h6' gutterBottom>
            Fechas
          </Typography>
          <DateField source='createdAt' label='Fecha de creación' showTime />
          <DateField source='updatedAt' label='Última actualización' showTime />
          <DateField source='resolvedAt' label='Fecha de resolución' showTime />
        </Box>

        {/* Mostrar notas de resolución si existen */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Notas de Resolución
          </Typography>
          <TextField source='resolutionNotes' label='Notas' />
        </Box>
      </Box>
    </SimpleShowLayout>
  </Show>
);
